import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { HeaderBar } from '../components/HeaderBar';
import { ProgressStepper } from '../components/ProgressStepper';
import { ActionButton } from '../components/ActionButton';
import { useNegotiation } from '../context/NegotiationContext';
interface Message {
  id: string;
  sender: 'ai' | 'user';
  text: string;
  timestamp: Date;
}
export const NegotiationScreen: React.FC = () => {
  const navigate = useNavigate();
  const {
    setHasSubmittedOffer,
    setNegotiationCompleted
  } = useNegotiation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showReviseButton, setShowReviseButton] = useState(false);
  const [totalCost, setTotalCost] = useState('123.525€');
  const [isRevised, setIsRevised] = useState(false);
  const [revisionCount, setRevisionCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Simulate initial AI message after component mount
  useEffect(() => {
    // Short delay to simulate AI thinking
    const timer = setTimeout(() => {
      setIsTyping(true);
      // Simulate AI typing
      setTimeout(() => {
        setIsTyping(false);
        setMessages([{
          id: '1',
          sender: 'ai',
          text: 'Thank you for your submission. Your proposed price is currently 15% above the target budget. Are you able to revise your offer to better align with our budget constraints?',
          timestamp: new Date()
        }]);
      }, 2000);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  const handleSendMessage = () => {
    if (!inputValue.trim()) return;
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      sender: 'user',
      text: inputValue,
      timestamp: new Date()
    };
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setInputValue('');
    // Simulate AI response
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      // Check if the message is about a discount
      if (inputValue.toLowerCase().includes('discount') || inputValue.toLowerCase().includes('10%') || inputValue.toLowerCase().includes('reduce') || inputValue.toLowerCase().includes('lower')) {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          sender: 'ai',
          text: "That's a great offer. I can update the proposal to a new total price of 123.525€ (which includes a 10% discount). Please confirm to proceed with this revision.",
          timestamp: new Date()
        };
        setMessages(prevMessages => [...prevMessages, aiResponse]);
        setShowReviseButton(true);
        // Highlight and update the total cost
        setTimeout(() => {
          setTotalCost('123.525€');
        }, 500);
      } else if (inputValue.toLowerCase().includes('no') || inputValue.toLowerCase().includes('cannot') || inputValue.toLowerCase().includes("can't")) {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          sender: 'ai',
          text: 'I understand your constraints. Could you share what specific factors are preventing a price reduction? Perhaps we can find alternative solutions like extended payment terms or phased delivery.',
          timestamp: new Date()
        };
        setMessages(prevMessages => [...prevMessages, aiResponse]);
      } else {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          sender: 'ai',
          text: "I understand. Could you provide more details about what adjustments you might be able to make to the pricing? We're specifically looking for options that could reduce the total cost while maintaining the quality requirements.",
          timestamp: new Date()
        };
        setMessages(prevMessages => [...prevMessages, aiResponse]);
      }
    }, 2000);
  };
  const handleReviseProposal = () => {
    setIsRevised(true);
    setShowReviseButton(false);
    setRevisionCount(prev => prev + 1);
    // Add final AI message
    const finalMessage: Message = {
      id: Date.now().toString(),
      sender: 'ai',
      text: "Your revised proposal has been sent to the buyer. They will review your updated pricing and respond shortly. You'll receive a notification when they provide feedback.",
      timestamp: new Date()
    };
    setMessages(prevMessages => [...prevMessages, finalMessage]);
    // Update negotiation state
    setHasSubmittedOffer(true);
    if (isRevised) {
      setNegotiationCompleted(true);
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const handleReturnToOverview = () => {
    // Update state before navigating
    setHasSubmittedOffer(true);
    setNegotiationCompleted(true);
    navigate('/');
  };
  return <div className="min-h-screen bg-[#f5f6f8]" data-id="element-295">
      <HeaderBar logoSrc="https://uploadthingy.s3.us-west-1.amazonaws.com/tXtrQ38XxwUBTwmzoNN8ZL/Prospeum_Logo_White_SVG.svg" logoAlt="Prospeum Logo" data-id="element-296" />
      <main className="container mx-auto px-4 py-8" data-id="element-297">
        <div className="mb-8" data-id="element-298">
          <h1 className="text-2xl font-bold text-[#333333] mb-6" data-id="element-299">
            Business Laptop - Negotiation
          </h1>
          <ProgressStepper steps={['Submit Proposal', 'Verify Details', 'Submission Complete']} currentStep={2} data-id="element-300" />
          {!isRevised && <div className="bg-[#4caf50] text-white px-4 py-3 rounded-md mb-6" data-id="element-301">
              <div className="flex" data-id="element-302">
                <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-303">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-304" />
                </svg>
                <span data-id="element-305">Your proposal has been successfully submitted!</span>
              </div>
            </div>}
          {isRevised && <div className="bg-[#4caf50] text-white px-4 py-3 rounded-md mb-6" data-id="element-306">
              <div className="flex" data-id="element-307">
                <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-308">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-309" />
                </svg>
                <span data-id="element-310">Your revised proposal has been sent to the buyer.</span>
              </div>
            </div>}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-311">
          {/* Left Column - Submission Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-312">
            <h2 className="text-lg font-bold text-[#333333] mb-4 flex items-center" data-id="element-313">
              <svg className="w-5 h-5 mr-2 text-[#005a9e]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-314">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" data-id="element-315" />
              </svg>
              Proposal Details
              {revisionCount > 0 && <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full" data-id="element-316">
                  Revision {revisionCount}
                </span>}
            </h2>
            <div className="mb-4" data-id="element-317">
              <div className="flex justify-between items-center py-2 border-b border-gray-200" data-id="element-318">
                <span className="text-[#333333] font-medium" data-id="element-319">Status:</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${isRevised ? 'bg-[#4caf50] bg-opacity-10 text-[#4caf50]' : 'bg-[#fbc02d] bg-opacity-10 text-[#fbc02d]'}`} data-id="element-320">
                  {isRevised ? 'Revised Proposal Submitted' : 'Submitted to Buyer'}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-321">
                <span className="text-[#333333] font-medium" data-id="element-322">Item 1:</span>
                <span className="text-[#333333]" data-id="element-323">Dell XPS 14 v2025</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-324">
                <span className="text-[#333333] font-medium" data-id="element-325">Item 2:</span>
                <span className="text-[#333333]" data-id="element-326">
                  Extended Warranty Package
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-327">
                <span className="text-[#333333] font-medium" data-id="element-328">Quantity:</span>
                <span className="text-[#333333]" data-id="element-329">150 units</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-330">
                <span className="text-[#333333] font-medium" data-id="element-331">
                  Unit Price (Laptops):
                </span>
                <span className="text-[#333333]" data-id="element-332">
                  {isRevised ? '824€' : '915€'}
                  {isRevised && <span className="ml-2 text-xs text-green-600 line-through" data-id="element-333">
                      915€
                    </span>}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-334">
                <span className="text-[#333333] font-medium" data-id="element-335">Warranty:</span>
                <span className="text-[#333333]" data-id="element-336">
                  {isRevised ? '0€ per unit' : '0€ per unit'}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-337">
                <span className="text-[#333333] font-medium" data-id="element-338">
                  Delivery Date:
                </span>
                <span className="text-[#333333]" data-id="element-339">10/17/2025</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-340">
                <span className="text-[#333333] font-medium" data-id="element-341">
                  Payment Terms:
                </span>
                <span className="text-[#333333]" data-id="element-342">
                  Net 30 days from invoice date
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-343">
                <span className="text-[#333333] font-medium" data-id="element-344">
                  Delivery Method:
                </span>
                <span className="text-[#333333]" data-id="element-345">
                  Direct shipping to corporate headquarters
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-346">
                <span className="text-[#333333] font-medium" data-id="element-347">
                  Warranty Details:
                </span>
                <span className="text-[#333333]" data-id="element-348">
                  2-year on-site service with next business day response
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-349">
                <span className="text-[#333333] font-medium" data-id="element-350">
                  Special Remarks:
                </span>
                <span className="text-[#333333] text-right max-w-[60%]" data-id="element-351">
                  Includes pre-installation of company software suite and custom
                  BIOS configuration
                </span>
              </div>
              {isRevised && <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-352">
                  <span className="text-[#333333] font-medium" data-id="element-353">
                    Discount Applied:
                  </span>
                  <span className="text-green-600 font-medium" data-id="element-354">10%</span>
                </div>}
              <div className="flex justify-between py-3 mt-2" data-id="element-355">
                <span className="text-[#333333] font-bold" data-id="element-356">Total Cost:</span>
                <div data-id="element-357">
                  <span className={`text-[#333333] font-bold ${totalCost === '123.525€' ? 'bg-yellow-100 px-2 py-1 rounded transition-all duration-500' : ''}`} data-id="element-358">
                    {isRevised ? '123.525€' : '137.250€'}
                  </span>
                  {isRevised && <div className="text-xs text-green-600 line-through text-right" data-id="element-359">
                      137.250€
                    </div>}
                </div>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-360">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-361">
                Timeline
              </h3>
              <div className="space-y-3" data-id="element-362">
                <div className="flex" data-id="element-363">
                  <div className="flex-shrink-0" data-id="element-364">
                    <div className="h-4 w-4 rounded-full bg-green-500 mt-1" data-id="element-365"></div>
                  </div>
                  <div className="ml-3" data-id="element-366">
                    <p className="text-sm font-medium text-gray-900" data-id="element-367">
                      Proposal Submitted
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-368">
                      {new Date().toLocaleDateString()}{' '}
                      {new Date().toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                    </p>
                  </div>
                </div>
                {isRevised && <div className="flex" data-id="element-369">
                    <div className="flex-shrink-0" data-id="element-370">
                      <div className="h-4 w-4 rounded-full bg-green-500 mt-1" data-id="element-371"></div>
                    </div>
                    <div className="ml-3" data-id="element-372">
                      <p className="text-sm font-medium text-gray-900" data-id="element-373">
                        Revision Submitted
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-374">
                        {new Date().toLocaleDateString()}{' '}
                        {new Date().toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                      </p>
                    </div>
                  </div>}
                <div className="flex" data-id="element-375">
                  <div className="flex-shrink-0" data-id="element-376">
                    <div className="h-4 w-4 rounded-full bg-gray-300 mt-1" data-id="element-377"></div>
                  </div>
                  <div className="ml-3" data-id="element-378">
                    <p className="text-sm font-medium text-gray-500" data-id="element-379">
                      Buyer Review
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-380">Pending</p>
                  </div>
                </div>
                <div className="flex" data-id="element-381">
                  <div className="flex-shrink-0" data-id="element-382">
                    <div className="h-4 w-4 rounded-full bg-gray-300 mt-1" data-id="element-383"></div>
                  </div>
                  <div className="ml-3" data-id="element-384">
                    <p className="text-sm font-medium text-gray-500" data-id="element-385">
                      Award Decision
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-386">Pending</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Right Column - Chat Interface */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-[600px]" data-id="element-387">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-388">
              <h2 className="text-lg font-bold text-[#333333] flex items-center" data-id="element-389">
                <svg className="w-5 h-5 mr-2 text-[#005a9e]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-390">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" data-id="element-391" />
                </svg>
                AI Negotiation Assistant
              </h2>
              {isTyping && <span className="text-xs text-gray-500 animate-pulse" data-id="element-392">
                  AI is typing...
                </span>}
            </div>
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50" data-id="element-393">
              {messages.map(message => <div key={message.id} className={`mb-4 ${message.sender === 'ai' ? 'flex justify-start' : 'flex justify-end'}`} data-id="element-394">
                  <div className={`max-w-[80%] rounded-lg px-4 py-2 ${message.sender === 'ai' ? 'bg-white border border-gray-200 text-[#333333]' : 'bg-[#005a9e] text-white'}`} data-id="element-395">
                    <p className="whitespace-pre-wrap" data-id="element-396">{message.text}</p>
                    <p className="text-xs opacity-70 mt-1 text-right" data-id="element-397">
                      {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                    </p>
                  </div>
                </div>)}
              {isTyping && <div className="flex justify-start mb-4" data-id="element-398">
                  <div className="bg-white border border-gray-200 rounded-lg px-4 py-3" data-id="element-399">
                    <div className="flex space-x-1" data-id="element-400">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                    animationDelay: '0ms'
                  }} data-id="element-401"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                    animationDelay: '150ms'
                  }} data-id="element-402"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                    animationDelay: '300ms'
                  }} data-id="element-403"></div>
                    </div>
                  </div>
                </div>}
              <div ref={messagesEndRef} data-id="element-404" />
            </div>
            <div className="p-4 border-t border-gray-200" data-id="element-405">
              {!isRevised ? <div className="flex flex-col space-y-4" data-id="element-406">
                  <div className="flex" data-id="element-407">
                    <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={handleKeyPress} placeholder="Type your message..." className="flex-1 border border-gray-300 rounded-l-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#005a9e] focus:border-transparent" disabled={isRevised} data-id="element-408" />
                    <button onClick={handleSendMessage} className="bg-[#005a9e] text-white px-4 py-2 rounded-r-md hover:bg-[#004a85] focus:outline-none focus:ring-2 focus:ring-[#005a9e] focus:ring-offset-2 disabled:opacity-50" disabled={!inputValue.trim() || isRevised} data-id="element-409">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-410">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" data-id="element-411" />
                      </svg>
                    </button>
                  </div>
                  {showReviseButton && <div data-id="element-412">
                      <ActionButton label="Submit Revised Proposal (123.525€)" onClick={handleReviseProposal} className="w-full" data-id="element-413" />
                    </div>}
                  {!showReviseButton && messages.length > 0 && <div className="text-xs text-gray-500 text-center" data-id="element-414">
                      <p data-id="element-415">Suggested responses:</p>
                      <div className="flex flex-wrap justify-center gap-2 mt-2" data-id="element-416">
                        <button onClick={() => {
                    setInputValue('I can offer a 10% discount on the laptops, bringing the total to 123.525€.');
                  }} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700" data-id="element-417">
                          Offer 10% discount
                        </button>
                        <button onClick={() => {
                    setInputValue("We can't reduce the price further due to our cost constraints.");
                  }} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700" data-id="element-418">
                          Explain constraints
                        </button>
                        <button onClick={() => {
                    setInputValue('What specific price point are you targeting?');
                  }} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700" data-id="element-419">
                          Ask for target price
                        </button>
                      </div>
                    </div>}
                </div> : <div className="text-center py-2 text-gray-500" data-id="element-420">
                  <p data-id="element-421">Negotiation complete. Awaiting buyer response.</p>
                </div>}
            </div>
          </div>
        </div>
        {/* Return to Overview Button */}
        <div className="mt-8 flex justify-center" data-id="element-422">
          <ActionButton label="Return to Overview" onClick={handleReturnToOverview} variant="secondary" className="px-8" icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-424">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" data-id="element-425" />
              </svg>} data-id="element-423" />
        </div>
      </main>
    </div>;
};